import { CompletionContext } from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import { SyntaxNode } from '@lezer/common';

export const Identifier = /^[\w$\xa1-\uffff][\w$\d\xa1-\uffff.]*$/;
export const StringIdentifier = /'[\S]*'/;
export const BracketStringIdentifier = /[?'[\S]*'?/;

export interface Path {
  path: readonly string[];
  name: string;
}

/**
 * Helper function for defining JavaScript completion sources. It
 * returns the completable name and object path for a completion
 * context, or null if no name/property completion should happen at
 * that position. For example, when completing after `a.b.c` it will
 * return `{path: ["a", "b"], name: "c"}`. When completing after `x`
 * it will return `{path: [], name: "x"}`. When not in a property or
 * name, it will return null if `context.explicit` is false, and
 * `{path: [], name: ""}` otherwise.
 *
 * NOTE(brandon): This function is duplicated from `@codemirror/lang-javascript`
 *                with additional support for string property accessors.
 */
export function completionPath(
  context: CompletionContext,
  dontComplete: string[] = [],
): Path | null {
  const read = (node: SyntaxNode) => context.state.doc.sliceString(node.from, node.to);
  const inner = syntaxTree(context.state).resolveInner(context.pos, -1);
  if (inner.name === 'PropertyName') {
    return pathFor(read, inner.parent!, read(inner));
  } else if (
    (inner.name === '.' || inner.name === '?.' || inner.name === '[') &&
    inner.parent!.name === 'MemberExpression'
  ) {
    return pathFor(read, inner.parent!, '');
  } else if (dontComplete.indexOf(inner.name) > -1) {
    return null;
  } else if (
    inner.name === 'VariableName' ||
    (inner.to - inner.from < 20 && Identifier.test(read(inner)))
  ) {
    return { path: [], name: read(inner) };
  } else if (
    inner.name === 'String' ||
    StringIdentifier.test(context.state.sliceDoc(inner.from, inner.to))
  ) {
    return pathFor(read, inner.parent!, read(inner));
  } else if (inner.name === 'MemberExpression') {
    return pathFor(read, inner, '');
  } else {
    return context.explicit ? { path: [], name: '' } : null;
  }
}

function pathFor(read: (node: SyntaxNode) => string, member: SyntaxNode, name: string) {
  const path = [];
  for (;;) {
    const obj = member?.firstChild;
    let prop;
    if (obj?.name === 'VariableName') {
      path.push(read(obj));
      return { path: path.reverse(), name };
    } else if (
      obj?.name === 'MemberExpression' &&
      (prop = obj.lastChild)?.name === 'PropertyName'
    ) {
      path.push(read(prop!));
      member = obj;
    } else if (
      obj?.name === 'MemberExpression' &&
      obj.lastChild?.name === ']' &&
      (prop = obj.lastChild.prevSibling)?.name === 'String'
    ) {
      path.push(read(prop!));
      member = obj;
    } else {
      return null;
    }
  }
}
